import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Clock, Loader2, Save } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { sessionSettings as sessionSettingsSchema } from '../lib/types'
import { TextField } from './ui/Field'
import { SettingsUpdatedAt } from './SettingsUpdatedAt'

const btnPrimary =
  'shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium ' +
  'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] ' +
  'cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'

// The token-lifetime fields are positive whole numbers. Block the sign /
// exponent / decimal keys, and strip any non-digit that slips through (e.g. a
// paste), so a value can never go negative or non-integer.
function blockNonIntegerKey(e) {
  if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault()
}
const digitsOnly = (v) => String(v).replace(/[^0-9]/g, '')

/**
 * Session settings — the access-token and refresh-token lifetimes that drive
 * every login and refresh. Rendered as a tab on the Settings page; reads and
 * writes the `session` singleton via `/super-admin/session-settings`. A change
 * applies to new tokens only — sessions already signed in keep the lifetime
 * they were issued with.
 */
export function SessionSettings() {
  const query = useQuery({
    queryKey: ['session-settings'],
    queryFn: async () => {
      const res = await api.get('/super-admin/session-settings')
      return sessionSettingsSchema.parse(res.data)
    },
  })
  if (query.isLoading) {
    return <p className="text-sm text-[color:var(--color-ink-muted)]">Loading…</p>
  }
  if (query.isError) {
    return (
      <p className="text-sm text-[color:var(--color-priority-urgent)]">
        {extractApiError(query.error).message}
      </p>
    )
  }
  return <SessionSettingsForm key={query.dataUpdatedAt} settings={query.data} />
}

function SessionSettingsForm({ settings }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(() => ({
    accessTokenExpiryMinutes: String(settings.accessTokenExpiryMinutes ?? 15),
    refreshTokenExpiryDays: String(settings.refreshTokenExpiryDays ?? 7),
  }))
  const [errors, setErrors] = useState({})

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        accessTokenExpiryMinutes: Number(form.accessTokenExpiryMinutes),
        refreshTokenExpiryDays: Number(form.refreshTokenExpiryDays),
      }
      const res = await api.put('/super-admin/session-settings', body)
      return res.data
    },
    onSuccess: () => {
      toast.success('Session settings saved. New logins use the updated lifetimes.')
      setErrors({})
      void qc.invalidateQueries({ queryKey: ['session-settings'] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields && Object.keys(fields).length > 0) {
        setErrors(fields)
        toast.error('Please fix the highlighted fields.')
      } else {
        toast.error(message)
      }
    },
  })

  function set(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
    if (errors[key]) {
      const { [key]: _omit, ...rest } = errors
      void _omit
      setErrors(rest)
    }
  }

  const isDirty =
    form.accessTokenExpiryMinutes !==
      String(settings.accessTokenExpiryMinutes ?? 15) ||
    form.refreshTokenExpiryDays !== String(settings.refreshTokenExpiryDays ?? 7)

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
            <Clock
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Session
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            How long a session stays valid before sign-in is required.
          </p>
        </div>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || saveMutation.isPending}
          className={btnPrimary}
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save size={14} />
              Save Changes
            </>
          )}
        </button>
      </div>

      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl">
          <div>
            <TextField
              label="Access Token (Minutes)"
              type="number"
              min={5}
              max={1440}
              placeholder="15"
              value={form.accessTokenExpiryMinutes}
              onChange={(e) =>
                set('accessTokenExpiryMinutes', digitsOnly(e.target.value))
              }
              onKeyDown={blockNonIntegerKey}
              error={errors.accessTokenExpiryMinutes}
            />
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              How often the session silently renews.
              <span className="block">5–1440 minutes; 15 recommended.</span>
            </p>
          </div>
          <div>
            <TextField
              label="Refresh Token (Days)"
              type="number"
              min={1}
              max={365}
              placeholder="7"
              value={form.refreshTokenExpiryDays}
              onChange={(e) =>
                set('refreshTokenExpiryDays', digitsOnly(e.target.value))
              }
              onKeyDown={blockNonIntegerKey}
              error={errors.refreshTokenExpiryDays}
            />
            <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
              Total time a user stays signed in.
              <span className="block">1–365 days; 7 recommended.</span>
            </p>
          </div>
        </div>

        <p className="mt-4 text-xs text-[color:var(--color-ink-subtle)]">
          Applies to new sign-ins and refreshes; existing sessions keep their
          current lifetime.
        </p>
      </div>
      <SettingsUpdatedAt at={settings.updatedAt} />
    </div>
  )
}
